import { Axios } from '@/plugins/axios'
import { Bus as AppEventBus, Events as AppEvents } from '@/events/AppEvents'
import { i18n } from '@/plugins/i18n'
import { getDeviceFingerprintWithin } from '@/services/Security/DeviceFingerprintService'
import { getEmbeddedClient } from '@/services/Security/EmbeddedClient'
import { isAxiosTimeoutError, isServerTimeoutStatus, reportRequestTimeout } from '@/plugins/axios/RequestTimeoutMonitor'

import BadRequestException from '@/exceptions/BadRequestException'
import ForbiddenException from '@/exceptions/ForbiddenException'
import GlobalException from '@/exceptions/GlobalException'
import NotFoundException from '@/exceptions/NotFoundException'
import NotImplementedException from '@/exceptions/NotImplementedException'
import RequestTimeoutException from '@/exceptions/RequestTimeoutException'
import ServerErrorException from '@/exceptions/ServerErrorException'
import TooManyRequestException from '@/exceptions/TooManyRequestException'
import UnauthorizedException from '@/exceptions/UnauthorizedException'
import UnprocessableEntityException from '@/exceptions/UnprocessableEntityException'

const getErrorMessage = data => {
	let result = null
	// TODO: We may have to add a prefix on the errorCode
	if (data?.error?.message) {
		result = data.error.message
	} else if (data?.code) {
		if (i18n.t(data.code)) {
			result = i18n.t(data.code)
		} else if (data?.message) {
			result = data.message
		}
	} else if (data?.message) {
		result = data.message
	}
	return result
}
const getErrorCode = data => {
	return data?.error?.code || data?.code
}

const getUnprocessableEntityMessage = errors => {
	const keys = Object.keys(errors)
	const fieldError = errors[keys[1] ?? keys[0]]
	return (Array.isArray(fieldError) ? fieldError[0] : fieldError) ?? null
}

const handleClientError = (status, data) => {
	let result = null
	let message = getErrorMessage(data)
	let code = getErrorCode(data)
	switch (status) {
		case 400:
			result = new BadRequestException(message)
			break
		case 401:
			result = new UnauthorizedException(message)
			AppEventBus.emit(AppEvents.LOGOUT)
			break
		case 403:
			result = new ForbiddenException(message, code)
			if (code === 'PASSWORD_EXPIRED') {
				AppEventBus.emit(AppEvents.PASSWORD_EXPIRED, data?.expired_at ?? null)
			}
			break
		case 404:
			result = new NotFoundException(message, code)
			break
		case 408:
			result = new RequestTimeoutException(message)
			break
		case 422: {
			if (data && (data.errors || data.error)) {
				const errors = data.errors ? data.errors : data.error
				message = getUnprocessableEntityMessage(errors)
				result = new UnprocessableEntityException(message, errors)
			}
			break
		}
		case 429:
			result = new TooManyRequestException()
			break
		default:
			result = new GlobalException(message)
			break
	}
	return result
}

const handleServerError = (status, data) => {
	let message = null
	let result = null

	// TODO: We may have to add a prefix on the errorCode
	if (data?.error?.code && i18n.te(data.error.code)) {
		message = i18n.t(data.error.code)
	} else if (data?.error?.message) {
		message = data.error.message
	} else if (data?.message) {
		message = data.message
	}

	if (status) {
		switch (status) {
			case 500:
				if (!message || message.toLowerCase() == 'server error') {
					result = new ServerErrorException()
				} else {
					result = new ServerErrorException(message)
				}

				break
			case 501:
				result = new NotImplementedException(message)
				break
			default:
				result = new GlobalException(message)
				break
		}
	}
	return result
}

/**
 * @param {Number} status
 * @param {Object} data
 */
const handleResponse = (status, data) => {
	let exception = null
	let result = null

	if (status) {
		if (status >= 100 && status < 200) {
			// Informational
		} else if (status >= 200 && status < 300) {
			// Success
		} else if (status >= 300 && status < 400) {
			// Redirection
		} else if (status >= 400 && status < 500) {
			exception = handleClientError(status, data)
		} else if (status >= 500 && status < 600) {
			exception = handleServerError(status, data)
		} else {
			exception = new GlobalException()
		}
	}

	if (exception) {
		throw exception
	}
	return result
}

const onRequestError = error => {
	if (!Axios.isCancel(error)) {
		throw new GlobalException()
	}
	return Promise.reject(error)
}

const DEVICE_FINGERPRINT_HEADER = 'X-Device-Fingerprint'
const EMBEDDED_CLIENT_HEADER = 'X-Embedded-Client'

// Tells the server which device the account is used from (shared accounts report) and whether the webapp is embedded
// (mobile app webview): the mobile app is not the webapp of a computer.
const addClientDeviceHeaders = async config => {
	const embeddedClient = getEmbeddedClient()
	if (embeddedClient) {
		config.headers[EMBEDDED_CLIENT_HEADER] = embeddedClient
	}

	const deviceFingerprint = await getDeviceFingerprintWithin()
	if (deviceFingerprint) {
		config.headers[DEVICE_FINGERPRINT_HEADER] = deviceFingerprint
	}
}

const onRequestSuccess = config => {
	config.headers.Accept = 'application/json'

	const accessToken = localStorage.getItem('token')
	if (accessToken && (!config.hasOwnProperty('forbiddenHeaders') || !config.forbiddenHeaders.includes('Authorization'))) {
		config.headers.Authorization = accessToken
	}

	const oauthProvider = localStorage.getItem('OAuthProvider')
	if (oauthProvider && (!config.hasOwnProperty('forbiddenHeaders') || !config.forbiddenHeaders.includes('OAuthProvider'))) {
		config.headers.OAuthProvider = oauthProvider
	}

	return config
}

// Interceptor of the requests to the Welyb API (not the WOPI host, which does not know these headers).
const onApiRequestSuccess = async config => {
	await addClientDeviceHeaders(onRequestSuccess(config))

	return config
}

const extractResponseErrorHttpData = error => {
	let config = null
	let httpData = null
	let httpStatus = null
	if (!Axios.isCancel(error) && error) {
		config = error.response?.config || error.config
		httpData = error.response?.data || error.error
		httpStatus = error.response?.status || error.status
	}
	return {
		config: config,
		httpData: httpData,
		httpStatus: httpStatus
	}
}

const isPasswordExpiredException = exception => exception instanceof ForbiddenException && exception.code === 'PASSWORD_EXPIRED'

const reportTimeout = (error, config, httpStatus) => {
	if (isAxiosTimeoutError(error)) {
		reportRequestTimeout({
			client: 'axios',
			method: error.config?.method,
			url: error.config?.url,
			params: error.config?.params,
			timeout: error.config?.timeout
		})
	} else if (isServerTimeoutStatus(httpStatus)) {
		reportRequestTimeout({ client: 'server', method: config?.method, url: config?.url, params: config?.params, status: httpStatus })
	}
}

const onResponseError = error => {
	let result = null
	const { config, httpData, httpStatus } = extractResponseErrorHttpData(error)
	reportTimeout(error, config, httpStatus)
	try {
		if (httpStatus) {
			if (config?.show_error !== false && config?.params?.show_error !== false) {
				if (httpStatus === 'resumable') {
					AppEventBus.emit(AppEvents.SNACKBAR_ERROR, httpData)
					result = Promise.reject(error)
				} else {
					result = handleResponse(httpStatus, httpData)
				}
			} else {
				result = Promise.reject(error)
			}
		} else if (Axios.isCancel(error)) {
			result = Promise.reject(error)
		}
	} catch (exception) {
		const showSnackbar =
			!isPasswordExpiredException(exception) &&
			config?.show_error_in_snackbar !== false &&
			exception.message &&
			!exception.message.includes(i18n.t('errors.global_error'))
		if (showSnackbar) {
			AppEventBus.emit(AppEvents.SNACKBAR_ERROR, exception.message)
		}
		result = Promise.reject(exception)
	}
	return result
}

const onResponseSuccess = async response => {
	if (response?.config?.cache && 'caches' in self) {
		const cache = await caches.open('welyb-cache')
		cache.put(response.request, new Response(response.data, { headers: response.headers, status: response.status }))
	}
	return response
}

export default {
	onRequestError: onRequestError,
	onApiRequestSuccess: onApiRequestSuccess,
	onRequestSuccess: onRequestSuccess,
	onResponseError: onResponseError,
	onResponseSuccess: onResponseSuccess,
	getErrorMessage: getErrorMessage
}
